import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState
} from "react";

import { useDispatch, useSelector } from "react-redux";
import {
  Checkbox,
  FormControl,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectProps
} from "@material-ui/core";
import { AppState } from "../../store";
import { Store } from "../../models/Store";
import { thunkFetchStores } from "../../store/stores/actions";
import { StoresState } from "../../store/stores/types";

interface StoreMultiSelectProps extends Omit<SelectProps, "loading"> {
  filterOpened?: boolean;
  filterClusters?: ReadonlyArray<string>;
  storeList?: ReadonlyArray<Store>;
  label?: string;
  onSelected?: (value: ReadonlyArray<Store>) => void;
  variant?: "filled" | "outlined" | "standard";
  value?: ReadonlyArray<string>;
}

export const StoreMultiSelect: FunctionComponent<StoreMultiSelectProps> = ({
  filterOpened,
  filterClusters,
  storeList,
  label = "Store",
  onSelected,
  onChange,
  variant = "standard",
  value = [],
  ...props
}) => {
  const dispatch = useDispatch();
  const storesState = useSelector<AppState, StoresState>(s => s.stores);
  let stores = storeList ?? storesState.stores;

  const [state, setState] = useState<ReadonlyArray<Store>>([]);

  const handleChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => {
    if (onSelected) {
      let storeIds = event.target.value as Array<string>;
      let stores = state.filter(s => storeIds.includes(s.id));

      onSelected(stores ?? []);
    }
    if (onChange) {
      onChange(event, child);
    }
  };

  const renderNames = (storeIds: ReadonlyArray<string>): ReactNode => {
    return state
      .filter(store => storeIds.includes(store.id))
      .map(store => store.name)
      .join(", ");
  };

  useEffect(() => {
    let filtered =
      stores &&
      stores.length > 0 &&
      stores
        .filter(
          store =>
            filterOpened === undefined ||
            (filterOpened &&
              (store.state === "Opened" || store.state === "InSetup")) ||
            (!filterOpened && store.state === "Closed")
        )
        .filter(
          store =>
            !filterClusters ||
            filterClusters.length === 0 ||
            filterClusters.some(clusterId => store.cluster.id === clusterId)
        );

    if (filtered) {
      setState(filtered);
    }
  }, [stores, filterClusters]);

  useEffect(() => {
    // load if not loaded
    if (!stores || !stores.length) {
      dispatch(thunkFetchStores());
    }
  }, []);

  const rnd = Math.floor(Math.random() * 100 + 1);
  const id = `${rnd}-store-multi-select`;

  return (
    <FormControl variant={variant} className={props.className}>
      <InputLabel
        id={`${id}-label`}
        style={
          variant === "outlined"
            ? {
                backgroundColor: "#fff",
                paddingLeft: "0.5rem",
                paddingRight: "0.5rem"
              }
            : undefined
        }
      >
        {label}
      </InputLabel>
      <Select
        error={props.error}
        multiple
        value={value}
        onChange={handleChange}
        labelId={`${id}-label`}
        id={id}
        autoWidth={true}
        input={<Input />}
        disabled={props.disabled}
        renderValue={value => renderNames(value as string[])}
      >
        {state &&
          state.length > 0 &&
          state.map(store => (
            <MenuItem key={`${rnd}-${store.id}`} value={store.id}>
              <Checkbox checked={value.indexOf(store.id) > -1} />
              <ListItemText primary={store.name} />
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};
